import { ASnackbar } from '@adornis/adornis-components/a-snackbar';
import { AdornisRouter } from '@adornis/base/client/a-router';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { BehaviorController } from '@adornis/chemistry/controllers/behavior-controller';
import { DataLoadingController } from '@adornis/chemistry/controllers/data-loading-controller';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import { SizeController } from '@adornis/chemistry/controllers/size-controller';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/x-empty-placeholder';
import '@adornis/file-utils/client/x-image';
import { EntityValidator } from '@adornis/forms/entity-validator';
import '@adornis/forms/x-form';
import '@adornis/germanium/components/ge-button';
import '@adornis/germanium/components/ge-date-picker-dropdown';
import '@adornis/germanium/components/ge-dropdown-selection';
import '@adornis/germanium/components/ge-input';
import '@adornis/germanium/components/ge-loading-state';
import '@adornis/germanium/components/ge-section';
import '@adornis/germanium/components/ge-toggle-button';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map } from 'rxjs/operators';
import '../components/wtg-footer';
import { WelcomeBox } from './../../db/welcome-box';
import { WTGUser } from './../../db/wtg-user';

@customElement('wtg-welcome-box')
export class WTGWelcomeBox extends ChemistryLitElement {
  size = new SizeController(this);
  styles = () => ({ ':host': { height: '100%' } });

  user = new DataLoadingController(this, () => WTGUser.currentUser.whenReady);
  @state() welcomeBox = new BehaviorController(this, new WelcomeBox({}));

  // @state() _whenChanging = new ObservableController(this, this.welcomeBox.pipe(switchMap(box => box.dirty)));

  @state() isSuccess = new ObservableController(
    this,
    AdornisRouter.get().path.pipe(map(path => path === '/welcome_box/success')),
  );

  public render() {
    if (this.user.isLoading) return html` <ge-loading-state></ge-loading-state> `;
    if (!this.user.value)
      return html`
        <x-empty-placeholder
          no-wrapper
          .img=${'warning'}
          .text=${html`
            <ge-text> Please log in first. </ge-text>
            <ge-button href="/login"> Login </ge-button>
          `}
          ><x-empty-placeholder> </x-empty-placeholder
        ></x-empty-placeholder>
      `;

    const content = html`
      <ge-section>
        <x-flex space="xl" ${css({ paddingTop: this.size.isMobile ? '200px' : '' })}>
          ${this.isSuccess.value ? this.success() : this.form()}
        </x-flex>
        <div ${css({ height: '50px' })}></div>
      </ge-section>
    `;

    if (this.size.isTablet) return content;
    else
      return html`
        <x-flex horizontal space="xxl" crossaxis-center ${css({ height: '100%' })}> ${this.fluff()} ${content} </x-flex>
      `;
  }

  private fluff() {
    return html`
      <x-flex center ${css({ background: this.colors.secondary, borderBottomRightRadius: '150px', height: '100%' })}>
        <x-image
          .value=${require('client/assets/svgs_home/welcome_box.svg')}
          mode="width"
          ${css({ width: '700px', marginRight: '-150px' })}
        >
        </x-image>
      </x-flex>
    `;
  }

  private form() {
    this.welcomeBox.value.userID = this.user.value!._id;

    return html`
      <ge-h1>
        Order your <br />
        Welcome Box
      </ge-h1>
      <x-form
        .validator=${EntityValidator.fromEntity(WelcomeBox)}
        .document=${this.welcomeBox.value}
        @success=${async (e: CustomEvent<WelcomeBox>) => {
          try {
            await e.detail.create();
            AdornisRouter.goTo('/welcome_box/success');
          } catch (e) {
            ASnackbar.show(`Oops, an error has occurred: ${(e as Error).message}`);
          }
        }}
        @error=${(e: CustomEvent<Error>) => ASnackbar.show(`Error: ${e.detail.message}`)}
      >
        <x-flex space="md" ${css({ maxWidth: this.sizes.maxReadableWidth })}>
          <x-flex space="sm">
            <ge-toggle-button
              a-form-key="alreadyInGermany"
              label="Are you already in Germany?"
              @value-picked=${() => this.requestUpdate()}
            ></ge-toggle-button>
          </x-flex>

          ${this.welcomeBox.value.alreadyInGermany
            ? nothing
            : html`
                <x-flex space="sm">
                  <ge-text> Pre order your box, when is your arrival? </ge-text>
                  <ge-date-picker-dropdown a-form-key="preOrderArrival" placeholder="Please select...">
                  </ge-date-picker-dropdown>
                </x-flex>
              `}

          <x-flex space="sm">
            <ge-text> Select a city to pick it up: </ge-text>
            <ge-dropdown-selection
              a-form-key="pickUpCity"
              placeholder-mode="hide"
              .selectables=${['Frankfurt', 'München', 'Berlin']}
              placeholder="Please select..."
            >
            </ge-dropdown-selection>
          </x-flex>

          <x-flex space="sm">
            <ge-text> Select partner location or event: </ge-text>
            <ge-dropdown-selection
              a-form-key="partnerLocation"
              .selectables=${['Barmer']}
              placeholder-mode="hide"
              placeholder="Please select..."
            >
            </ge-dropdown-selection>
          </x-flex>

          <div>
            <ge-button validate> Submit </ge-button>
          </div>
        </x-flex>
      </x-form>
    `;
  }

  private success() {
    return html`
      <ge-h1>
        Thank you 😊 <br />
        for your order!
      </ge-h1>
      <ge-text> Your order has been placed and is being processed. </ge-text>
      <ge-text> You will receive an email 📨 with more details. </ge-text>
      <ge-button href="/" ${css({ alignSelf: 'start' })}> Go home </ge-button>
    `;
  }
}
